import React, { useState } from 'react';
import {
  Box, Typography, Stack, Paper, Table, TableHead, TableRow, TableCell, Chip, IconButton, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Select, FormControl,
  InputLabel, CircularProgress, Alert, Snackbar, Tooltip, Switch,
  FormControlLabel,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CategoryIcon from '@mui/icons-material/Category';
import { useQuery, useMutation } from '@apollo/client';
import {
  ALL_SERVICE_CATEGORIES_QUERY,
  CREATE_SERVICE_CATEGORY_MUTATION,
  UPDATE_SERVICE_CATEGORY_MUTATION,
  DELETE_SERVICE_CATEGORY_MUTATION,
} from '../graphql/queries';

const ICON_OPTIONS = [
  'People', 'LocationOn', 'Restaurant', 'CameraAlt', 'Palette', 'MusicNote',
  'Badge', 'DirectionsCar', 'Face', 'Flight', 'AutoFixHigh', 'TempleHindu',
  'Mail', 'Hotel', 'EventSeat', 'EventNote', 'CardGiftcard', 'MoreHoriz',
  'Spa', 'RoomService', 'Storefront', 'LocalParking', 'Celebration', 'Star',
];

const COLOR_OPTIONS = [
  'default', 'primary', 'secondary', 'success', 'error', 'info', 'warning',
];

const EMPTY_FORM = {
  name: '',
  displayName: '',
  description: '',
  icon: '',
  color: 'default',
  sortOrder: '0',
  isActive: true,
};

export default function AdminServiceCategoriesPage() {
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  const [delConfirm, setDelConfirm] = useState(null);

  const { data, loading, error, refetch } = useQuery(ALL_SERVICE_CATEGORIES_QUERY, {
    variables: { activeOnly: false },
    fetchPolicy: 'network-only',
  });

  const [createCategory, { loading: creating }] = useMutation(CREATE_SERVICE_CATEGORY_MUTATION, {
    onCompleted: () => { setDlgOpen(false); refetch(); toast('Category created'); },
    onError: (e) => toast(e.message, 'error'),
  });
  const [updateCategory, { loading: updating }] = useMutation(UPDATE_SERVICE_CATEGORY_MUTATION, {
    onCompleted: () => { setDlgOpen(false); refetch(); toast('Category updated'); },
    onError: (e) => toast(e.message, 'error'),
  });
  const [deleteCategory] = useMutation(DELETE_SERVICE_CATEGORY_MUTATION, {
    onCompleted: () => { setDelConfirm(null); refetch(); toast('Category deleted'); },
    onError: (e) => toast(e.message, 'error'),
  });

  function toast(msg, severity = 'success') {
    setSnack({ open: true, msg, severity });
  }

  function openNew() {
    setEditItem(null);
    setForm(EMPTY_FORM);
    setDlgOpen(true);
  }

  function openEdit(cat) {
    setEditItem(cat);
    setForm({
      name: cat.name,
      displayName: cat.displayName,
      description: cat.description || '',
      icon: cat.icon || '',
      color: cat.color || 'default',
      sortOrder: String(cat.sortOrder || 0),
      isActive: cat.isActive,
    });
    setDlgOpen(true);
  }

  function handleSave() {
    const input = {
      name: form.name.trim().toLowerCase().replace(/\s+/g, '_'),
      displayName: form.displayName.trim(),
      description: form.description.trim() || null,
      icon: form.icon || null,
      color: form.color || 'default',
      sortOrder: parseInt(form.sortOrder, 10) || 0,
      isActive: form.isActive,
    };
    if (editItem) {
      updateCategory({ variables: { input: { id: editItem.id, ...input } } });
    } else {
      createCategory({ variables: { input } });
    }
  }

  const categories = data?.allServiceCategories || [];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <CategoryIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Service Categories</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage the categories available when creating wedding services
            </Typography>
          </Box>
        </Stack>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>
          Add Category
        </Button>
      </Stack>

      {/* Table */}
      {loading && <CircularProgress sx={{ display: 'block', mx: 'auto', mt: 4 }} />}
      {error && <Alert severity="error">{error.message}</Alert>}
      {!loading && !error && (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 700 }}>Order</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Name (key)</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Display Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Icon</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Color</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No categories found. Click "Add Category" to create one.
                  </TableCell>
                </TableRow>
              )}
              {categories.map((cat) => (
                <TableRow key={cat.id} hover>
                  <TableCell>{cat.sortOrder}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{cat.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{cat.displayName}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                      {cat.description || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={cat.icon || '—'} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip label={cat.color || 'default'} size="small"
                      color={COLOR_OPTIONS.includes(cat.color) ? cat.color : 'default'} />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={cat.isActive ? 'Active' : 'Inactive'}
                      color={cat.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(cat)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setDelConfirm(cat)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dlgOpen} onClose={() => setDlgOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editItem ? 'Edit Category' : 'New Service Category'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Display Name *"
              value={form.displayName}
              onChange={(e) => {
                const displayName = e.target.value;
                const name = editItem ? form.name : displayName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
                setForm({ ...form, displayName, name });
              }}
              fullWidth
              helperText="The user-facing label for this category"
            />
            <TextField
              label="Key Name *"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
              helperText="Unique identifier (lowercase, underscores). e.g. venue, catering"
              disabled={!!editItem}
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              multiline rows={2}
              fullWidth
            />
            <Stack direction="row" spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Icon</InputLabel>
                <Select
                  value={form.icon}
                  label="Icon"
                  onChange={(e) => setForm({ ...form, icon: e.target.value })}
                >
                  <MenuItem value="">None</MenuItem>
                  {ICON_OPTIONS.map((ic) => (
                    <MenuItem key={ic} value={ic}>{ic}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Color</InputLabel>
                <Select
                  value={form.color}
                  label="Color"
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                >
                  {COLOR_OPTIONS.map((c) => (
                    <MenuItem key={c} value={c} sx={{ textTransform: 'capitalize' }}>{c}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Sort Order"
                type="number"
                value={form.sortOrder}
                onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
                fullWidth
              />
            </Stack>
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                />
              }
              label="Active (visible to users)"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!form.name || !form.displayName || creating || updating}
          >
            {creating || updating ? <CircularProgress size={18} /> : (editItem ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!delConfirm} onClose={() => setDelConfirm(null)} maxWidth="xs">
        <DialogTitle>Delete Category?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <b>{delConfirm?.displayName}</b>? Services using this category will retain their category value but it won't appear in dropdowns.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDelConfirm(null)}>Cancel</Button>
          <Button
            color="error" variant="contained"
            onClick={() => deleteCategory({ variables: { id: delConfirm.id } })}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
